import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useIntl, FormattedMessage } from 'react-intl'
import { useLazyQuery, useMutation } from '@apollo/react-hooks'
import { Query } from 'react-apollo'

import { GET_LEARNING_PATH_BY_LPIDS } from '@queries/learningPath'
import { GET_ELECTIVES } from '@queries/electives'
import { PUT_REGISTRATION_MATERIALS } from '@queries/registrations'

import PortalLearningPath from '@classes/LearningPath'
import MultiSelectElective, {
  ModalSelectElectiveProps as MultiSelectElectiveProps,
} from '@components/Modal/ModalSelectElective'
import SingleSelectElective, {
  ModalSelectElectiveProps as SingleSelectElectiveProps,
} from '@berlitzplatforms/micro.modal.select-elective/lib'
import LoadingModal from '../LoadingModal'
import WarningModal from '../WarningModal'
import useEnsureNoDisplayedModal from '@hooks/useEnsureNoDisplayedModal'
import { ElectiveOption } from '@components/Modal/ModalSelectElective/interface'
import { registrationSelector } from '@components/ActiveState/activeStateSlice'
import { ElectivesQueryResult } from '@interfaces/Electives'
import { MY_BERLITZ_ENV } from '@utils/constants'
import { getStorageAdapter } from '@shell/adapters/storage'

export interface SelectElectiveModalProps {
  MainMaterialId: string
  StudentProfileId: string
  Path: PortalLearningPath
  autoOpen?: boolean
  open?: boolean
  onClose?: () => void
}

export const SelectElectiveModal: React.FC<SelectElectiveModalProps> = ({
  MainMaterialId,
  StudentProfileId,
  Path,
  autoOpen,
  open: manualOpen,
  onClose = () => {},
}) => {
  const intl = useIntl()
  const [open, setOpen] = useState(!!manualOpen)
  const [learningPathData, setLearningPathData] = useState<any>()
  const [ismodalReady, setIsModalReady] = useState(false)
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState<ElectiveOption[]>()
  const [notStartedElectives, setNotStartedElectives] = useState<ElectiveOption[]>([])
  const [defaultVal, setDefaultVal] = useState<string>()
  const [error, setError] = useState(false)
  const [isReenrolled, setIsReenrolled] = useState(false)

  const showMulti = useSelector(registrationSelector.selectIsElectiveMulti)
  const assignedElectives = useSelector(registrationSelector.selectElectiveMaterialsFromAllRegistrations) ?? []
  console.log('assignedElectives:', assignedElectives)
  const allElectiveWithSameLanguageAndType = useSelector(registrationSelector.AllRegistrationElectiveMaterials)

  const previouslySelected: ElectiveOption[] = assignedElectives?.map((elective) => ({
    value: elective?.MaterialId,
    label: elective?.MaterialName,
    key: elective?.MaterialId,
    selected: true,
  }))

  useEffect(() => {
    const references = Path?.Material?.RegistrationReferences || []

    const uniqueProgramKeys = new Set()
    let reenrolled = false

    for (const reg of references) {
      const key = `${reg.Language}-${reg.LanguageLevel}-${reg.ProgramType}`
      if (uniqueProgramKeys.has(key)) {
        reenrolled = true
        break
      }
      uniqueProgramKeys.add(key)
    }

    setIsReenrolled(reenrolled)
  }, [Path?.Material?.RegistrationReferences])

  const [getElectives, { data: electives, loading: loadingE, error: eError }] = useLazyQuery<ElectivesQueryResult>(
    GET_ELECTIVES,
    { fetchPolicy: 'no-cache' }
  )

  const [putElective, { loading: loadingPut, error: putError }] = useMutation(PUT_REGISTRATION_MATERIALS, {
    onError: () => {},
    onCompleted: async () => location.reload(),
  })

  useEffect(() => {
    if (MainMaterialId) {
      getElectives({ variables: { MainMaterialId, StudentProfileId } })
    }
  }, [MainMaterialId])

  useEffect(() => {
    setError(!!putError || !!eError)
  }, [putError, eError])

  useEffect(() => {
    setLoading(loadingE && !!manualOpen && !open)
  }, [loadingE, manualOpen, open])

  useEffect(() => {
    if (options?.length) {
      const tempFlag = getStorageAdapter().get(Path?.tempFlags?.SelectElective)
      const shouldOpen = !!autoOpen && !tempFlag
      setOpen(shouldOpen)
      if (shouldOpen) {
        getStorageAdapter().set(Path.tempFlags.SelectElective, 'true')
      }
    }
  }, [JSON.stringify(options)])

  useEffect(() => {
    if (typeof manualOpen === 'boolean') {
      setOpen(manualOpen && !!options?.length)
    }
  }, [manualOpen, JSON.stringify(options)])

  useEnsureNoDisplayedModal(
    () => {
      if (electives?.getElectives?.mainAndElectiveMaterialInfo?.electiveMaterials?.length) {
        const electiveOptions = electives.getElectives.mainAndElectiveMaterialInfo.electiveMaterials.map((opt) => ({
          value: opt.electiveMaterialId,
          label: opt.electiveMaterialName,
          key: opt.electiveMaterialId,
          selected: false,
        }))
        setOptions(electiveOptions as ElectiveOption[])
        setDefaultVal(electiveOptions[0].value)
        setIsModalReady(false)
      }
      if (!allElectiveWithSameLanguageAndType?.length && !assignedElectives?.length) {
        setIsModalReady(true)
      }
    },
    [electives],
    autoOpen && !!getStorageAdapter().get(Path.tempFlags.SelectElective)
  )

  const multiOnButtonClick = (electives: ElectiveOption[]) => {
    setOpen(false)
    onClose()
    const registrationMaterialInput = electives
      .filter((elective) => !elective.selected)
      .map((elective) => ({
        RegistrationMaterial: {
          RegistrationId: Path.Material?.RegistrationId,
          MaterialId: elective?.value,
        },
      }))
    putElective({ variables: { registrationMaterialInput } })
  }

  const singleOnButtonClick = (selected: any) => {
    setOpen(false)
    onClose()
    putElective({
      variables: {
        registrationMaterialInput: [
          {
            RegistrationMaterial: {
              RegistrationId: Path.Material?.RegistrationId,
              MaterialId: selected,
            },
          },
        ],
      },
    })
  }

  const props = {
    open,
    options,
    onClose: () => {
      setOpen(false)
      onClose()
    },
    title: (
      <FormattedMessage
        id="Welcome to {name}!"
        defaultMessage="Welcome to {name}!"
        values={{ name: Path?.Material?.MaterialName }}
      />
    ),
    description: (
      <FormattedMessage
        id="Before you start your level please select the elective course you'd like to take."
        defaultMessage="Before you start your level please select the elective course you'd like to take."
      />
    ),
    buttonText: <FormattedMessage id="Begin your course" defaultMessage="Begin your course" />,
    onButtonClick: singleOnButtonClick,
    selectLabel: intl.formatMessage({ id: 'Select an elective' }),
  }

  const MemoModal = useMemo(() => {
    if (!defaultVal) return null
    if (showMulti) {
      const modifiedProps: MultiSelectElectiveProps = {
        ...props,
        maxLimit: 3,
        onButtonClick: multiOnButtonClick,
      }
      return <MultiSelectElective {...modifiedProps} />
    } else {
      const allOptions = [...notStartedElectives, ...previouslySelected, ...(options ? options : [])]
      const modifiedProps: SingleSelectElectiveProps = {
        ...props,
        /* In the previous enrollment if elective is started and inprogress then pass only that elective as an option
           with the electives which are not started at the previous levels. 
        */
        options: options?.length === 1 ? [...notStartedElectives, ...options] : allOptions,
        defaultValue: defaultVal,
      }
      return <SingleSelectElective {...modifiedProps} />
    }
  }, [JSON.stringify(options), Path?.Id, defaultVal, open, ismodalReady])

  useEffect(() => {
    if (learningPathData) {
      const lpResp = learningPathData?.getLearningPathByIds ?? []
      const assignedIds = new Set(assignedElectives.map((ele) => ele.MaterialId))
      const inProgressElectives = assignedElectives.reduce<ElectiveOption[]>((acc, elective) => {
        const match = lpResp.find((lp) => lp.lpid === elective.LPID)
        const allowedStatus = ['inprogress', 'complete']
        const isInProgress = match?.details?.units?.some((unit) => allowedStatus.includes(unit?.unitstatus))
        if (isInProgress) {
          acc.push({
            value: elective.MaterialId,
            label: elective.MaterialName,
            key: elective.MaterialId,
            selected: false,
          })
        }
        return acc
      }, [])

      if (inProgressElectives.length > 0 && !showMulti) {
        setOptions(inProgressElectives as ElectiveOption[])
        setDefaultVal(inProgressElectives[0]?.value)
      }

      const notYetStartedElectives = allElectiveWithSameLanguageAndType.reduce<ElectiveOption[]>((acc, elective) => {
        const alreadyAssigned = assignedIds.has(elective.MaterialId)
        const match = lpResp.find((lp) => lp.lpid === elective.LPID)
        const allowedStatus = ['notyetstarted', 'unlocked', 'locked']
        const isNotStarted =
          Array.isArray(match?.details?.units) &&
          match.details.units.length > 0 &&
          match.details.units.every((unit) => allowedStatus.includes(unit?.unitstatus))

        if (isNotStarted && !alreadyAssigned) {
          acc.push({
            value: elective.MaterialId,
            label: elective.MaterialName,
            key: elective.MaterialId,
            selected: false,
          })
        }
        return acc
      }, [])
      setNotStartedElectives(notYetStartedElectives as ElectiveOption[])
      setIsModalReady(true)
    }
  }, [learningPathData, assignedElectives, allElectiveWithSameLanguageAndType, showMulti])

  return (
    <Query
      query={GET_LEARNING_PATH_BY_LPIDS}
      fetchPolicy="network-only"
      variables={{
        StudentId: Path?.StudentProfile?.SFId,
        IdType: 'student',
        Ids:
          isReenrolled && assignedElectives?.length
            ? assignedElectives.map((ele) => ele.LPID).join('|')
            : allElectiveWithSameLanguageAndType.map((ele) => ele.LPID).join('|'),
        berlitzEnv: Path?.StudentProfile?.MyBerlitzEnv ?? MY_BERLITZ_ENV.DEFAULT,
      }}
      onCompleted={(data) => {
        setLearningPathData(data)
      }}
      onError={() => {}}
    >
      {() => (
        <>
          {ismodalReady && MemoModal}
          <LoadingModal
            open={loading || loadingPut}
            onClose={() => {}}
            title={intl.formatMessage({ id: loadingPut || error ? 'Updating course...' : 'Fetching electives...' })}
            subtitle={intl.formatMessage({ id: 'Please wait a moment' }) + '.'}
          />
          <WarningModal open={error} onClose={() => setError(false)} anApiError noButtons />
        </>
      )}
    </Query>
  )
}
